
import Vue from "vue";
import { BrowseStateBase } from "@/utils/browse/browse-state-base";
import { ViewSidebar, ViewSidebarBlock } from "@/utils/views/view-sidebar";
import { ViewColumn } from "@/utils/views/view-column";
import { getModel, Model } from "@/utils/models/model";
import { resolveSidebarFieldValue } from "@/utils/views/sidebar-field-value";
import { AuthService } from "@/services/auth-service";
import { ISelectItem } from "winfakt-vue-components";
import AppBrowseSidebarBlock from "./sidebar/SidebarBlock.vue";

const MIN_WIDTH = 240;

export default Vue.extend({
	data(){
		return {
			resizing: false,
			dragWidth: 0,
			menuOpen: false
		};
	},
	props: {
		state: {
			type: Object as () => BrowseStateBase,
			required: true
		}
	},
	computed: {
		sidebar():ViewSidebar {
			return this.state.sidebar;
		},
		item():any {
			return this.state.sidebarItem;
		},
		blocks():ViewSidebarBlock[] {
			return this.sidebar.Blocks;
		},
		isLeft():boolean {
			return this.sidebar.Position == "left";
		},
		selectedCount():number {
			return this.state.selectedItems.length;
		},
		model():Model {
			return getModel(this.state.dataSource.modelName);
		},
		storageKey():string {
			return `browse-sidebar-width-${this.state.querySettings.view.ID}`;
		},
		width():number {
			if (this.dragWidth > 0) return this.dragWidth;
			return this.sidebar.Width || 380;
		},
		panelStyle():any {
			let style:any = {flexShrink: 0, width: `${this.width}px`};
			if (this.isLeft) {
				style.marginRight = "5px";
			} else {
				style.marginLeft = "5px";
			}
			return style;
		},
		viewColumns():ViewColumn[] {
			return this.state.querySettings.view.Columns;
		},
		titleField():ViewColumn | null {
			return this.sidebar.TitleField || this.viewColumns[0] || null;
		},
		subtitleFields():ViewColumn[] {
			if (this.sidebar.SubtitleFields.length > 0) return this.sidebar.SubtitleFields;
			if (this.sidebar.TitleField) {
				return this.viewColumns.filter(c=>c.Property != this.sidebar.TitleField!.Property).slice(0, 2);
			}
			return this.viewColumns.slice(1, 3);
		},
		title():string {
			let fallback = this.$ct("sidebar.title").toString();
			if (!this.item || !this.titleField) return fallback;
			return this.resolveText(this.titleField) || fallback;
		},
		subtitle():string {
			if (!this.item) return "";
			return this.subtitleFields.map(f=>this.resolveText(f)).filter(v=>v).join(" · ");
		},
		canEditView():boolean {
			let view = this.state.querySettings.view;
			return view.ID != 0 && view.UserID == AuthService.wfUser.ID;
		},
		dataSource():any {
			return this.state.dataSource;
		},
		canEdit():boolean {
			if (!this.dataSource.edit) return false;
			if (this.dataSource.canEdit && this.item && !this.dataSource.canEdit(this.item)) return false;
			return true;
		},
		menuItems():ISelectItem<Function>[] {
			let items:ISelectItem<Function>[] = [];
			if (this.item && this.dataSource.info) {
				items.push({text: this.$ct("sidebar.open-info").toString(), icon: "eye", value: ()=>this.dataSource.info(this.item)});
			}
			if (this.canEditView) {
				items.push({text: this.$ct("sidebar.configure").toString(), icon: "cog", value: ()=>this.configure()});
			}
			return items;
		}
	},
	watch: {
		storageKey: {
			immediate: true,
			handler(){
				this.loadWidth();
			}
		}
	},
	methods: {
		resolveText(field:ViewColumn):string {
			return resolveSidebarFieldValue(field, this.item, this.model).text;
		},
		close(){
			this.state.closeSidebar();
		},
		edit(){
			if (!this.item || !this.canEdit) return;
			this.dataSource.edit(this.item);
		},
		configure(){
			let view = this.state.querySettings.view;
			this.$router.push(`/settings/browse/${view.BrowseID}/view/${view.ID}?tab=sidebar`);
		},
		loadWidth(){
			try {
				let stored = parseInt(localStorage.getItem(this.storageKey) || "");
				this.dragWidth = isNaN(stored) ? 0 : stored;
			} catch (err) {
				this.dragWidth = 0;
			}
		},
		saveWidth(){
			if (this.dragWidth <= 0) return;
			try {
				localStorage.setItem(this.storageKey, `${this.dragWidth}`);
			} catch (err) {}
		},
		onResizeStart(e:MouseEvent){
			e.preventDefault();
			this.resizing = true;
			let startX = e.clientX;
			let startWidth = (this.$el as HTMLElement).offsetWidth;
			const onMouseMove = (e:MouseEvent)=>{
				let delta = e.clientX - startX;
				if (this.isLeft) {
					this.dragWidth = Math.max(MIN_WIDTH, startWidth + delta);
				} else {
					this.dragWidth = Math.max(MIN_WIDTH, startWidth - delta);
				}
			};
			const onMouseUp = ()=>{
				this.resizing = false;
				this.saveWidth();
				window.removeEventListener("mousemove", onMouseMove);
				window.removeEventListener("mouseup", onMouseUp);
			};
			window.addEventListener("mousemove", onMouseMove);
			window.addEventListener("mouseup", onMouseUp);
		}
	},
	components: {
		AppBrowseSidebarBlock
	},
	i18nEx: {
		componentPrefix: "components.browse"
	}
});
